/** @jsx jsx */
import {
  ArrowBackIcon,
  Box,
  Button,
  ChevronDownIcon,
  Container,
  DottedList,
  DottedListItem,
  Flex,
  Heading,
  Paragraph,
  TypeApplicationIcon,
} from "@eworkit/iris";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { DialogDisclosure } from "reakit/Dialog";
import { MenuButton } from "reakit/Menu";
import { jsx } from "theme-ui";

import { ConfirmationDialogModal } from "@/components/ConfirmModal";
import { DropdownMenu, DropdownMenuItem, useDropdownMenuState } from "@/components/dropdowns";
import { UserContext } from "@/components/User";
import { deleteApplication } from "@/data/job-applications";
import { ExitModalActionType } from "@/data/job-applications/types/ExitModalActionType";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { useToasts } from "@/views/components/Toast/useToasts";
import { JobApplicationDetailsTabs } from "@/views/job-applications/details/_components/JobApplicationDetailsTabs";
import { JobApplicationStatusComponent } from "@/views/job-applications/status/JobApplicationStatusComponent";
import { router } from "@/views/router";

import { ApplyModal } from "../../apply/ApplyModal";
import { useApplyModalState } from "../../apply/useApplyModalState";

interface JobApplicationDetailsHeaderProps {
  jobApplication: JobApplication;
  onSaved?: () => void;
}

export const JobApplicationDetailsHeader: FC<JobApplicationDetailsHeaderProps> = ({ jobApplication, onSaved }) => {
  const { t } = useTranslation("session");

  const { previousPath } = usePreviousRoutePath();
  const jobApplicationsPath = useMemo(() => previousPath, []);

  const history = useHistory();
  const actionsState = useDropdownMenuState();
  const { addToast } = useToasts();
  const { contextCompany } = useContext(UserContext);

  const applyModalState = useApplyModalState(jobApplication.jobRequest);

  const [modalData, setModalData] = useState({
    open: false,
    action: () => {
      return;
    },
    textMain: "",
  });

  const onModalClose = () => {
    setModalData(prevState => ({
      ...prevState,
      open: false,
    }));
  };

  const goToApplicationList = useCallback(() => {
    history.push(router.get("job-applications").path);
  }, [history]);

  const handleClosed = useCallback(
    (action: ExitModalActionType) => {
      if (action === "REMOVED") {
        addToast({ text: t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUCCESS_MESSAGE_DELETE"), type: "success" });
        goToApplicationList();
      } else if (action === "SAVED" || action === "SUBMITTED") {
        onSaved?.();
      }
    },
    [goToApplicationList, onSaved, addToast, t],
  );

  const handleDelete = async () => {
    try {
      await deleteApplication({ jobApplicationId: jobApplication.id, companyId: contextCompany && contextCompany.id });
      addToast({ text: t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUCCESS_MESSAGE_DELETE"), type: "success" });
      onModalClose();
      goToApplicationList();
    } catch (err) {
      addToast({ text: t("common:VALIDATION_MESSAGES.GENERAL"), type: "warning" });
    }
    goToApplicationList();
  };

  return (
    <Box bg="n0" borderColor="n100" borderBottom="primary" pt="sm">
      <Container>
        <Flex px="sm" flexDirection="column" gap="sm">
          <div>
            <Button
              as={Link}
              to={jobApplicationsPath ?? router.get("job-applications").path}
              variant="ghost"
              size="sm"
              iconLeft={<ArrowBackIcon />}>
              {t("JOB_APPLICATIONS.DETAIL.BACK_TO_APPLICATIONS")}
            </Button>
          </div>

          <Flex flexDirection={["column", null, null, "row"]} justifyContent="space-between" gap="sm">
            <Flex gap="sm">
              <TypeApplicationIcon color="vg500" size="xl" />

              <Flex flexDirection="column" gap="xxs">
                <Flex
                  flexDirection={["column", null, "row"]}
                  gap={["xxs", null, "xs"]}
                  alignItems={[null, null, "center"]}>
                  <Heading size="h-md" sx={{ fontWeight: 500 }}>
                    {jobApplication.jobRequest.title}
                  </Heading>

                  <div>
                    <JobApplicationStatusComponent status={jobApplication.status} />
                  </div>
                </Flex>

                <DottedList>
                  <DottedListItem>
                    <Paragraph color="n700">{jobApplication.jobRequest.systemId}</Paragraph>
                  </DottedListItem>
                </DottedList>
              </Flex>
            </Flex>

            {jobApplication.status === "DRAFT" && (
              <div>
                <Button as={MenuButton} variant="secondary" iconRight={<ChevronDownIcon />} {...actionsState}>
                  {t("JOB_APPLICATIONS.DETAIL.ACTIONS.BUTTON_LABEL")}
                </Button>

                <DropdownMenu {...actionsState}>
                  <DropdownMenuItem {...actionsState} as={DialogDisclosure} {...applyModalState}>
                    {t("JOB_APPLICATIONS.DETAIL.ACTIONS.EDIT")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    {...actionsState}
                    onClick={() => {
                      setModalData({
                        open: true,
                        action: handleDelete,
                        textMain: "DELETE",
                      });
                    }}>
                    {t("JOB_APPLICATIONS.DETAIL.ACTIONS.DELETE")}
                  </DropdownMenuItem>
                </DropdownMenu>

                <ConfirmationDialogModal
                  onAccept={modalData.action}
                  header={t(`JOB_APPLICATIONS.${modalData.textMain}.MODAL.HEADER`)}
                  icon="warning-2"
                  confirmationText={t(`JOB_APPLICATIONS.${modalData.textMain}.MODAL.CONFIRMATION`)}
                  acceptButtonLabel={t(`JOB_APPLICATIONS.${modalData.textMain}.MODAL.ACCEPT_BUTTON_LABEL`)}
                  cancelButtonLabel={t(`JOB_APPLICATIONS.${modalData.textMain}.MODAL.CANCEL_BUTTON_LABEL`)}
                  open={modalData.open}
                  onClose={onModalClose}
                />

                {applyModalState.visible && (
                  <ApplyModal
                    dialogState={applyModalState}
                    jobRequest={jobApplication.jobRequest}
                    initialDraft={jobApplication as any}
                    onClosed={handleClosed}
                  />
                )}
              </div>
            )}
          </Flex>

          <JobApplicationDetailsTabs jobApplication={jobApplication} />
        </Flex>
      </Container>
    </Box>
  );
};
